import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { type Invoice, keys } from "../api";
import { type SaveStatus, sendAutosave } from "../autosave";

/**
 * Autosave for the invoice review, the way Confluence does it (Tyler, 2026-09-24): type half a figure,
 * close the browser, open the draft in another one, and it is there, half a figure and all.
 *
 * What is saved is the screen, not the invoice: the pass the brewer was on, and every lot's code,
 * count and spec field exactly as typed, keyed by the lot card it belongs to. It goes two places:
 *
 *   - this browser's own storage, on every keystroke, so killing the browser outright loses nothing
 *     on this machine;
 *   - the server, half a second after typing stops and once more as the tab is hidden or closed, so
 *     any other browser picks up at the same point.
 *
 * On opening, whichever of the two is newer wins. The invoice's own data is untouched by any of it:
 * the lots pass still writes lots and specs when it is saved, and the saved screen is cleared once the
 * invoice is received.
 */

export type ReviewState = NonNullable<Invoice["reviewState"]>;

const DEBOUNCE_MS = 500;
const RETRY_MS = 5000;
const localKey = (invoiceId: string) => `bl.review.${invoiceId}`;

interface Stamped {
  state: ReviewState;
  savedAt: string;
}

function readLocal(invoiceId: string): Stamped | null {
  try {
    const raw = localStorage.getItem(localKey(invoiceId));
    return raw ? (JSON.parse(raw) as Stamped) : null;
  } catch {
    return null;
  }
}

function writeLocal(invoiceId: string, stamped: Stamped) {
  try {
    localStorage.setItem(localKey(invoiceId), JSON.stringify(stamped));
  } catch {
    // Storage full or private browsing: the server copy still goes.
  }
}

function clearLocal(invoiceId: string) {
  try {
    localStorage.removeItem(localKey(invoiceId));
  } catch {
    // Nothing to clear.
  }
}

/** The newer of what the server kept and what this browser kept. */
function newer(invoice: Invoice): ReviewState | null {
  const local = readLocal(invoice.id);
  const server =
    invoice.reviewState && invoice.reviewStateAt
      ? { state: invoice.reviewState, savedAt: invoice.reviewStateAt }
      : null;
  if (!local) return server?.state ?? null;
  if (!server) return local.state;
  return Date.parse(local.savedAt) > Date.parse(server.savedAt) ? local.state : server.state;
}

export function useReviewAutosave(invoice: Invoice | undefined) {
  const draft = invoice?.status === "draft";
  const invoiceId = invoice?.id;
  // Read once, when the draft first arrives: what the passes start from, and what every save builds
  // on. Both are set together — a working copy taken before the draft had loaded started at step 0,
  // and the first keystroke after reopening then saved the screen as though it were on the first pass.
  const initial = useRef<ReviewState | null | undefined>(undefined);
  const state = useRef<ReviewState>({ step: 0 });
  if (invoice && initial.current === undefined) {
    initial.current = draft ? newer(invoice) : null;
    state.current = initial.current ?? { step: 0 };
  }
  const pending = useRef<Stamped | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const mounted = useRef(true);
  const shown = useRef(invoiceId);
  shown.current = invoiceId;
  const [status, setStatus] = useState<SaveStatus>("idle");
  const queryClient = useQueryClient();

  const flush = useCallback(
    async (keepalive = false) => {
      clearTimeout(timer.current);
      const stamped = pending.current;
      if (!stamped || !invoiceId) return;
      // Only the invoice gone (404) or no longer a draft (409) ends it (Tyler, 2026-10-22). A screen
      // the server will not take as typed, such as a lot code past its length, keeps trying, kept on
      // this device with its note showing, rather than letting what was typed go.
      const result = await sendAutosave(
        "PUT",
        `/invoices/${invoiceId}/review-state`,
        stamped,
        keepalive,
        [404, 409],
      );
      // Only settled if nothing newer was typed while it was on its way.
      if (pending.current !== stamped) return;
      if (result === "saved") {
        pending.current = null;
        setStatus("saved");
      } else if (result === "rejected") {
        // Received or deleted elsewhere: nothing is left to pick up, and the page shows the invoice
        // as it is now.
        pending.current = null;
        clearLocal(invoiceId);
        setStatus("idle");
        void queryClient.invalidateQueries({ queryKey: keys.invoice(invoiceId) });
      } else {
        setStatus("retrying");
        // Left behind, or another invoice opened in its place, the copy in this browser waits for
        // the review to be opened again.
        if (mounted.current && shown.current === invoiceId) {
          timer.current = setTimeout(() => void flush(), RETRY_MS);
        }
      }
    },
    [invoiceId, queryClient],
  );

  const update = useCallback(
    (patch: Partial<ReviewState>) => {
      if (!invoiceId || !draft) return;
      state.current = { ...state.current, ...patch };
      const stamped = { state: state.current, savedAt: new Date().toISOString() };
      pending.current = stamped;
      writeLocal(invoiceId, stamped);
      setStatus("saving");
      clearTimeout(timer.current);
      timer.current = setTimeout(() => void flush(), DEBOUNCE_MS);
    },
    [invoiceId, draft, flush],
  );

  // Leaving (the tab hidden, the page closed, or just another screen of the app) sends whatever is
  // still waiting, and the tab closing is exactly when a normal request would be cut off.
  useEffect(() => {
    mounted.current = true;
    const away = () => {
      if (document.visibilityState === "hidden") void flush(true);
    };
    const gone = () => void flush(true);
    document.addEventListener("visibilitychange", away);
    window.addEventListener("pagehide", gone);
    return () => {
      document.removeEventListener("visibilitychange", away);
      window.removeEventListener("pagehide", gone);
      mounted.current = false;
      void flush(true);
    };
  }, [flush]);

  // Received or thrown away: this browser's copy has nothing left to pick up.
  useEffect(() => {
    if (invoiceId && invoice && !draft) clearLocal(invoiceId);
  }, [invoiceId, invoice, draft]);

  return { initial: initial.current ?? null, update, status };
}
